// src/pages/Production/ProductionStatusReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import ReportGenerator from '../../components/production/ReportGenerator';
import { generateProductionStatusReport } from '../../utils/reportingUtils';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b',
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e2e8f0'
    }
};

function ProductionStatusReport() {
    const { userData } = useAuth();
    const [productionOrders, setProductionOrders] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch real data from Firebase
    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch production orders
        const unsubOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'productionOrders'), orderBy('createdAt', 'desc')),
            snap => setProductionOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => {
                setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubOrders();
            unsubRecipes();
        };
    }, [userData]);

    // Process data for report
    const reportData = useMemo(() => {
        if (productionOrders.length === 0) return null;

        const processedOrders = productionOrders.map(order => {
            // Find recipe for this order
            const recipe = recipes.find(r =>
                r.id === order.recipeId ||
                r.name === order.recipeName ||
                r.product === order.productName
            );

            // Calculate progress
            const completedQuantity = order.completedQuantity || 0;
            const totalQuantity = order.quantity || 1;
            const progress = Math.round((completedQuantity / totalQuantity) * 100);

            // Determine status
            let status = order.status || 'Planlandı';
            if (progress >= 100) status = 'Tamamlandı';
            else if (progress > 0) status = 'Devam Ediyor';

            // Get end date
            let endDate = new Date();
            if (order.endDate) {
                endDate = order.endDate.toDate?.() || new Date(order.endDate);
            } else if (order.dueDate) {
                endDate = order.dueDate.toDate?.() || new Date(order.dueDate);
            }

            return {
                id: order.id,
                product: order.productName || 'Bilinmeyen Ürün',
                recipeName: recipe?.name || order.recipeName || 'Reçete Yok',
                quantity: totalQuantity,
                completedQuantity: completedQuantity,
                status: status,
                priority: order.priority || 'normal',
                startDate: order.startDate?.toDate?.() || order.createdAt?.toDate?.() || new Date(),
                endDate: endDate,
                assignedTo: order.assignedTo || order.stages?.[0]?.supplierName || '-'
            };
        });

        return generateProductionStatusReport(processedOrders);
    }, [productionOrders, recipes]);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Üretim emirleri yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/raporlar" style={styles.backLink}>
                    ← Üretim Raporları
                </Link>
                <div>
                    <h1 style={styles.title}>Üretim Durum Raporu</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Planlanan, devam eden ve tamamlanan üretim emirleri
                        {productionOrders.length > 0 && (
                            <span style={{ marginLeft: '10px' }}>
                                ({productionOrders.length} emir)
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {!reportData || productionOrders.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                    <h3 style={{ color: '#374151', marginBottom: '8px' }}>Üretim Emri Bulunamadı</h3>
                    <p>Henüz üretim emri oluşturulmamış.</p>
                    <Link
                        to="/uretim/emirler"
                        style={{
                            display: 'inline-block',
                            marginTop: '20px',
                            padding: '12px 24px',
                            background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
                            color: 'white',
                            borderRadius: '12px',
                            textDecoration: 'none',
                            fontWeight: '600'
                        }}
                    >
                        Üretim Emirlerine Git
                    </Link>
                </div>
            ) : (
                <ReportGenerator
                    reportType="production-status"
                    reportData={reportData}
                    onExport={(format) => console.log(`Exporting as ${format}`)}
                />
            )}
        </div>
    );
}

export default ProductionStatusReport;